/**
 * Caché en memoria para catálogos de referencia.
 *
 * Por qué existe: los catálogos (tareas, familias, centros de costo, estados,
 * mecánicos, obras…) se vuelven a pedir enteros cada vez que se abre un módulo,
 * una pestaña o un formulario, aunque cambien una vez por mes. Medido sobre la
 * demo: abrir una OTC bajaba 890 KB sólo de `tareas/`, y cada pestaña del
 * Almacén volvía a traer los mismos centros de costo y órdenes que la pestaña
 * anterior había traído cinco segundos antes.
 *
 * Qué resuelve, además del ahorro de bytes:
 *  - Deduplica las peticiones EN VUELO. Dos componentes que montan a la vez y
 *    piden el mismo catálogo comparten una sola petición, en lugar de disparar
 *    dos idénticas (el patrón que hacía pedir `familias/` por duplicado).
 *  - Un fallo no queda cacheado: si el cargador rechaza, la entrada se descarta
 *    y el siguiente intento vuelve a pedir.
 *
 * Qué NO va acá: datos transaccionales (movimientos, órdenes, requerimientos,
 * stock). Esos cambian con cada operación del taller y servirlos de un caché
 * mostraría saldos viejos. Este módulo es sólo para catálogos.
 */

const TTL_POR_DEFECTO_MS = 5 * 60 * 1000;

const memoria = new Map(); // clave -> { expira: number, valor: any }
const enVuelo = new Map(); // clave -> Promise

/**
 * Devuelve el catálogo cacheado, o lo pide con `cargador` y lo guarda.
 *
 * @param {string} clave     Identificador estable del catálogo, ej. 'tareas'.
 * @param {Function} cargador Función sin argumentos que devuelve una promesa.
 * @param {number} [ttlMs]   Vigencia. Pasar 0 fuerza el pedido.
 */
export async function obtenerCatalogo(clave, cargador, ttlMs = TTL_POR_DEFECTO_MS) {
  const ahora = Date.now();

  if (ttlMs > 0) {
    const entrada = memoria.get(clave);
    if (entrada && entrada.expira > ahora) {
      return entrada.valor;
    }
    // Alguien ya lo está pidiendo: sumarse a esa petición en vez de abrir otra.
    const pendiente = enVuelo.get(clave);
    if (pendiente) return pendiente;
  }

  const promesa = (async () => {
    const valor = await cargador();
    memoria.set(clave, { expira: Date.now() + ttlMs, valor });
    return valor;
  })();

  enVuelo.set(clave, promesa);

  try {
    return await promesa;
  } catch (err) {
    // Un error no se cachea: el próximo intento tiene que volver a pedir.
    memoria.delete(clave);
    throw err;
  } finally {
    enVuelo.delete(clave);
  }
}

/**
 * Invalida catálogos. Llamar después de crear/editar/borrar un registro de
 * configuración, para que la próxima pantalla vea el cambio.
 *
 * @param {string} [prefijo] Si se omite, limpia todo. Si se pasa, limpia las
 *                           claves que empiecen con ese prefijo.
 */
export function invalidarCatalogo(prefijo) {
  if (!prefijo) {
    memoria.clear();
    enVuelo.clear();
    return;
  }
  for (const clave of Array.from(memoria.keys())) {
    if (clave.startsWith(prefijo)) memoria.delete(clave);
  }
  for (const clave of Array.from(enVuelo.keys())) {
    if (clave.startsWith(prefijo)) enVuelo.delete(clave);
  }
}

const catalogoCache = { obtenerCatalogo, invalidarCatalogo };

export default catalogoCache;
